import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { type IEdgelessMediaLayer } from '../edgeless';
import './style.less';
import { getOperateType, useFramework } from '@/framework';
import { useProxyMouseEvents } from '@/utils/event-kit';

type Props = {
    layerId: string;
    onPlay?: (duration: number, url: string) => void;
};

const VIDEO_CONTROL_BAR_OPERATE_TYPE = 'video-control-bar';
const videoControlOperateAttrs = {
    'data-operate-type': VIDEO_CONTROL_BAR_OPERATE_TYPE,
};

const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const useVideoControlBarLogic = (layerId: string, onPlay?: (duration: number, url: string) => void) => {
    const framework = useFramework();
    const { edgelessCanvas, flatLayer } = framework.service;
    const layer = flatLayer.getLayer(layerId);
    const object = edgelessCanvas.getByLayerId(layerId) as unknown as IEdgelessMediaLayer;
    const videoEl: HTMLVideoElement | null = object ? Reflect.get(object, 'videoEl') : null;

    /**
     * 无限画布的缩放值，用于确定控制条的宽度
     */
    const [zoom, setZoom] = useState(edgelessCanvas.zoom);

    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const isDraggingRef = useRef(false);
    const dragTimeRef = useRef(0);

    /**
     * DOM ref: 进度条插槽
     */
    const slotRef = useRef<HTMLDivElement>(null);
    /**
     * DOM ref: 进度条填充
     */
    const fillRef = useRef<HTMLDivElement>(null);
    /**
     * DOM ref: 进度条滑块
     */
    const thumbRef = useRef<HTMLDivElement>(null);

    /**
     * 控制条宽度
     */

    const width = layer ? layer.width * zoom : 0;

    /**
     * [useEffect]: 监听无限画布缩放，同步缩放值
     */
    useEffect(() => {
        const id = edgelessCanvas.onZoom(setZoom);
        return () => framework.service.events.off(id);
    }, []);

    /**
     * [useEffect]: 初始化状态
     */
    useEffect(() => {
        if (!object || !videoEl) return;

        const dur = Math.round(videoEl.duration) || 0;
        const cTime = videoEl.currentTime || 0;

        isDraggingRef.current = false;
        dragTimeRef.current = 0;

        setIsPlaying(!videoEl.paused);
        setIsMuted(videoEl.muted || videoEl.volume === 0);
        setDuration(dur);
        setCurrentTime(cTime);
        updateProgressDisplay(cTime === dur ? 0 : cTime, dur);

        console.log(
            `EdgelessVideoControlBar [初始化状态]：duration=${dur}, currentTime=${cTime}, isPlaying=${!videoEl.paused}`,
        );
    }, [layerId, videoEl]);

    /**
     * 根据视频播放的当前时间和视频总时长更新进度条 UI
     * 1. 更新进度条 fill 的宽度
     * 2. 更新进度条滑块的位置
     *
     * @param time - 当前时间
     * @param dur - 视频总时长
     */
    const updateProgressDisplay = (time: number, dur: number) => {
        if (dur <= 0 || isDraggingRef.current) return;

        const percentage = Math.min(100, Math.max(0, (time / dur) * 100));

        if (thumbRef.current) thumbRef.current.style.left = `${percentage}%`;
        if (fillRef.current) fillRef.current.style.width = `${percentage}%`;
    };

    /**
     * [video native event]: loadedmetadata
     * 当视频元数据加载完成后，更新视频总时长和当前时间
     */
    const loadedmetadata = () => {
        if (!object || !videoEl) return;

        const dur = Math.round(videoEl.duration) || 0;
        const cTime = videoEl.currentTime || 0;

        setDuration(dur);
        updateProgressDisplay(cTime === dur ? 0 : cTime, dur);
    };

    /**
     * [video native event]: play
     * 更新播放状态
     */
    const play = () => {
        if (!object || !videoEl) return;
        setIsPlaying(true);
        if (onPlay) onPlay(duration, videoEl.src || '');
    };

    /**
     * [video native event]: pause
     * 更新播放状态
     */
    const pause = () => {
        if (!object || !videoEl) return;
        setIsPlaying(false);
    };

    /**
     * [video native event]: timeupdate
     * 当视频播放时间更新时，更新当前时间
     *
     * ATTENTION: 当视频正在拖动时，更新操作由拖拽事件触发，这里不再更新
     */
    const timeupdate = () => {
        if (!object || !videoEl) return;
        if (isDraggingRef.current) return;

        const dur = videoEl.duration || 0;
        const cTime = videoEl.currentTime || 0;

        setCurrentTime(cTime);
        updateProgressDisplay(cTime, dur);
    };

    /**
     * [video native event]: ended
     * 当视频播放结束时，更新播放状态
     */
    const ended = () => {
        if (!object || !videoEl) return;
        setIsPlaying(false);
    };

    /**
     * [video native event]: volumechange
     * 当视频静音状态或音量变化时，更新声音按钮状态
     */
    const volumechange = () => {
        if (!object || !videoEl) return;
        setIsMuted(videoEl.muted || videoEl.volume === 0);
    };

    /**
     * [useEffect]: 绑定视频原生事件
     */
    useEffect(() => {
        if (!videoEl) return;

        videoEl.addEventListener('loadedmetadata', loadedmetadata);
        videoEl.addEventListener('play', play);
        videoEl.addEventListener('pause', pause);
        videoEl.addEventListener('timeupdate', timeupdate);
        videoEl.addEventListener('ended', ended);
        videoEl.addEventListener('volumechange', volumechange);

        return () => {
            videoEl.removeEventListener('loadedmetadata', loadedmetadata);
            videoEl.removeEventListener('play', play);
            videoEl.removeEventListener('pause', pause);
            videoEl.removeEventListener('timeupdate', timeupdate);
            videoEl.removeEventListener('ended', ended);
            videoEl.removeEventListener('volumechange', volumechange);
        };
    }, [layerId, videoEl]);

    /**
     * 播放/暂停
     * @param flag - 播放/暂停
     */
    const handlePlayPause = (flag: boolean) => {
        if (!object || !videoEl) return;

        setIsPlaying(flag);

        if (flag) {
            void object.play().catch((error) => {
                console.error('EdgelessVideoControlBar play error', error);
                setIsPlaying(false);
            });
        } else {
            object.pause();
        }
    };

    /**
     * 切换静音状态
     */
    const handleMuteToggle = () => {
        if (!object || !videoEl) return;

        const muted = videoEl.muted || videoEl.volume === 0;
        const nextMuted = !muted;

        if (!nextMuted && videoEl.volume === 0) {
            videoEl.volume = 1;
        }

        videoEl.muted = nextMuted;
        setIsMuted(videoEl.muted || videoEl.volume === 0);
    };

    /**
     * 点击进度条插槽，修改当前播放时间
     * @param event - 事件对象
     */
    const handleMouseDownOnSlot = useCallback(
        (event: React.MouseEvent) => {
            if (!videoEl || !slotRef.current) return;

            isDraggingRef.current = true;
            setIsDragging(true);

            const rect = slotRef.current.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const percentage = Math.max(0, Math.min(1, x / rect.width));

            dragTimeRef.current = percentage * duration;

            const width = percentage * 100;
            if (thumbRef.current) thumbRef.current.style.left = `${width}%`;
            if (fillRef.current) fillRef.current.style.width = `${width}%`;
        },
        [duration],
    );

    /**
     * 点击进度条滑块，同步拖拽状态
     * @param event - 事件对象
     */
    const handleMouseDownOnThumb = useCallback(
        (event: React.MouseEvent) => {
            if (!videoEl || !thumbRef.current) return;

            event.stopPropagation();

            isDraggingRef.current = true;
            setIsDragging(true);

            dragTimeRef.current = currentTime;
        },
        [currentTime],
    );

    /**
     * 拖拽进度条插槽，修改进度条宽度及滑块位置
     * @param event - 事件对象
     */
    const handleMouseMove = useCallback(
        (event: MouseEvent) => {
            if (!isDraggingRef.current || !slotRef.current) return;

            const rect = slotRef.current.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const percentage = Math.max(0, Math.min(1, x / rect.width));

            dragTimeRef.current = percentage * duration;

            const width = percentage * 100;
            if (thumbRef.current) thumbRef.current.style.left = `${width}%`;
            if (fillRef.current) fillRef.current.style.width = `${width}%`;
        },
        [layerId, duration],
    );

    /**
     * 拖拽结束，修改当前播放时间
     */
    const handleMouseUp = useCallback(() => {
        if (!isDraggingRef.current || !videoEl) return;

        object.seek(dragTimeRef.current);

        isDraggingRef.current = false;

        setIsDragging(false);
        setCurrentTime(dragTimeRef.current);
    }, [layerId]);

    /**
     * [useEffect]: 统一处理 mousemove/mouseup
     */
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return {
        layer,
        object,
        videoEl,
        width,
        isPlaying,
        isMuted,
        isDragging,
        currentTime,
        duration,
        slotRef,
        fillRef,
        thumbRef,
        handlePlayPause,
        handleMuteToggle,
        handleMouseDownOnSlot,
        handleMouseDownOnThumb,
    };
};

export const EdgelessVideoControlBarV2: React.FC<Props> = ({ layerId, onPlay }) => {
    const {
        layer,
        object,
        videoEl,
        width,
        isPlaying,
        isMuted,
        isDragging,
        currentTime,
        duration,
        slotRef,
        fillRef,
        thumbRef,
        handlePlayPause,
        handleMuteToggle,
        handleMouseDownOnSlot,
        handleMouseDownOnThumb,
    } = useVideoControlBarLogic(layerId, onPlay);

    const checkOperateType = (e: React.MouseEvent) => {
        const type = getOperateType(e.target);
        return type !== VIDEO_CONTROL_BAR_OPERATE_TYPE;
    };
    const { mouseEvents } = useProxyMouseEvents({
        onMouseDown: checkOperateType,
        onMouseMove: checkOperateType,
        onMouseUp: checkOperateType,
    });

    if (!layer || !object || !videoEl || width < 120) {
        return null;
    }

    const isCompactMode = width < 250;

    return (
        <div
            className={classNames('media-layer-control-bar-v2', {
                'media-layer-control-bar-v2--compact': isCompactMode,
            })}
            {...mouseEvents}
            {...videoControlOperateAttrs}
            style={{ width }}
        >
            <div className="media-layer-control-bar-v2__operate" {...videoControlOperateAttrs}>
                <button
                    type="button"
                    className="media-layer-control-bar-v2__button"
                    {...videoControlOperateAttrs}
                    onClick={() => handlePlayPause(!isPlaying)}
                >
                    <IconFont name={isPlaying ? 'MediaPauseBold' : 'MediaPlayBold'} fontSize={16} />
                </button>

                <p className="media-layer-control-bar-v2__time">
                    <span className="media-layer-control-bar-v2__time-current">{formatTime(currentTime)}</span>
                    <span className="media-layer-control-bar-v2__time-separator">/</span>
                    <span className="media-layer-control-bar-v2__time-duration">{formatTime(duration)}</span>
                </p>

                <div className="media-layer-control-bar-v2__progress">
                    <div
                        className="media-layer-control-bar-v2__progress-slot"
                        ref={slotRef}
                        {...videoControlOperateAttrs}
                        onMouseDown={handleMouseDownOnSlot}
                    >
                        <div className="media-layer-control-bar-v2__progress-fill" ref={fillRef}></div>
                    </div>

                    <Popover
                        placement="top"
                        trigger="hover"
                        open={isDragging ? false : undefined}
                        overlayClassName="media-layer-control-bar-v2__progress-popover"
                        content={
                            <div className="media-layer-control-bar-v2__progress-time">
                                <span className="media-layer-control-bar-v2__progress-current">
                                    {formatTime(currentTime)}
                                </span>
                                <span className="media-layer-control-bar-v2__progress-duration">
                                    /{formatTime(duration)}
                                </span>
                            </div>
                        }
                    >
                        <div
                            className="media-layer-control-bar-v2__progress-thumb"
                            ref={thumbRef}
                            {...videoControlOperateAttrs}
                            onMouseDown={handleMouseDownOnThumb}
                        />
                    </Popover>
                </div>

                <button
                    type="button"
                    className="media-layer-control-bar-v2__button media-layer-control-bar-v2__sound-button"
                    {...videoControlOperateAttrs}
                    onClick={handleMuteToggle}
                >
                    <IconFont name={isMuted ? 'SpeakerMuteBold' : 'SpeakerBold'} fontSize={16} />
                </button>
            </div>
        </div>
    );
};
